import React, { useState, useRef, useEffect } from 'react';
import { trainingAPI, unwrapList } from '../services/api';
import Pagination, { paginateItems } from './Pagination';

function TrainingModules() {
  // Add a ref for the file input
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add state for modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Add state for form data with proper initialization
  const [newModule, setNewModule] = useState({
    title: '',
    description: '',
    is_active: true,
    youtube_video_url: '',
    imageFile: null,
    imagePreview: ''
  });
  
  const [editingModule, setEditingModule] = useState(null);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  
  // Add state for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Add state for viewing module details
  const [viewingModule, setViewingModule] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  // Fetch modules from API
  const fetchModules = async () => {
    setIsLoading(true);
    try {
      const response = await trainingAPI.getModules();
      setModules(unwrapList(response.data));
    } catch (err) {
      console.error("Error fetching modules:", err);
      setError('Failed to load training modules');
    } finally {
      setIsLoading(false);
    }
  };

  // Use useEffect to fetch modules on component mount
  useEffect(() => {
    fetchModules();
  }, []);

  // Filter modules based on search term, category, and status
  const filteredModules = (Array.isArray(modules) ? modules : []).filter(module => {
    const matchesSearch = module.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && module.is_active) || 
      (statusFilter === 'inactive' && !module.is_active);
    
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter]);

  const { pageItems, total, currentPage: safePage } = paginateItems(filteredModules, currentPage);

  // Handle adding a new module
  const handleAddModule = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', newModule.title);
      formData.append('description', newModule.description);
      formData.append('is_active', newModule.is_active);
      formData.append('youtube_video_url', newModule.youtube_video_url);
      
      if (newModule.imageFile) {
        formData.append('uploaded_images', newModule.imageFile);
      }
      
      // Log form data for debugging
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      await trainingAPI.addModule(formData);
      console.log("Module added successfully");
      
      fetchModules(); // Refresh the list
      setShowAddModal(false);
      setNewModule({
        title: '',
        description: '',
        is_active: true,
        youtube_video_url: '',
        imageFile: null,
        imagePreview: ''
      });
    } catch (err) {
      console.error("Error adding module:", err);
      setError(`Failed to add module: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click with proper initialization of all fields
  const handleEditClick = (module) => {
    console.log("Editing module:", module); // Debug log
    
    // Make sure we have the images array
    const images = module.images || [];
    console.log("Module images:", images); // Debug log
    
    setEditingModule({
      id: module.id,
      title: module.title || '',
      description: module.description || '',
      youtube_video_url: module.youtube_video_url || '',
      status: module.is_active ? 'active' : 'inactive',
      images: images,
      imageFile: null
      // Removed imagesToDelete array
    });
    setShowEditModal(true);
  };

  // Handle image upload for editing module
  const handleEditImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingModule({
          ...editingModule,
          imageFile: file,
          imagePreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Save edited module
  const handleSaveEdit = async () => {
    setIsLoading(true);
    try {
      console.log("Saving module with ID:", editingModule.id); // Debug log
      
      const formData = new FormData();
      formData.append('title', editingModule.title);
      formData.append('description', editingModule.description);
      formData.append('is_active', editingModule.status === 'active');
      formData.append('youtube_video_url', editingModule.youtube_video_url || '');
      
      // Only append image if a new one was selected
      if (editingModule.imageFile) {
        formData.append('uploaded_images', editingModule.imageFile);
      }
      
      // Log form data for debugging
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      const response = await trainingAPI.updateModule(editingModule.id, formData);
      console.log("Update response:", response); // Debug log
      
      // Refresh the list after successful update
      fetchModules();
      setShowEditModal(false);
      setEditingModule(null);
    } catch (err) {
      console.error("Error updating module:", err);
      setError(`Failed to update module: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm delete module
  const confirmDeleteModule = async () => {
    setIsLoading(true);
    try {
      console.log(`Toggling active status for module ${moduleToDelete.id}`);
      await trainingAPI.toggleModuleActive(moduleToDelete.id);
      console.log("Module status toggled successfully");
      
      // Refresh the list after successful toggle
      fetchModules();
      setShowDeleteModal(false);
      setModuleToDelete(null);
    } catch (err) {
      console.error("Error toggling module status:", err);
      setError(`Failed to delete module: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image upload for new module
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewModule({
          ...newModule,
          imageFile: file,
          imagePreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (module) => {
    setModuleToDelete(module);
    setShowDeleteModal(true);
  };

  // Handle view details click
  const handleViewDetails = (module) => {
    // Check if module has images
    const hasImages = module.images && module.images.length > 0;
    
    setViewingModule({
      ...module,
      currentImageIndex: 0
    });
    // Show video by default if no images are available
    setShowVideo(!hasImages && module.youtube_video_url);
    setCurrentImageIndex(0);
    setShowViewModal(true);
  };

  // Navigate to next image
  const nextImage = () => {
    if (viewingModule && viewingModule.images) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === viewingModule.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Navigate to previous image
  const prevImage = () => {
    if (viewingModule && viewingModule.images) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? viewingModule.images.length - 1 : prevIndex - 1
      );
    }
  };

  // Toggle between images and video
  const toggleVideoView = () => {
    setShowVideo(!showVideo);
  };

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Add function to remove image from new module
  const handleRemoveImage = () => {
    setNewModule({
      ...newModule,
      imageFile: null,
      imagePreview: ''
    });
  };

  // Add function to remove the new image in edit mode
  const handleRemoveEditNewImage = () => {
    setEditingModule({
      ...editingModule,
      imageFile: null,
      imagePreview: ''
    });
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Training Modules</h1>
        <button 
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Add New Module
        </button>
      </div>
      
      {/* Add Module Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Add New Training Module</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newModule.title}
                  onChange={(e) => setNewModule({...newModule, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Description *</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="3"
                  value={newModule.description}
                  onChange={(e) => setNewModule({...newModule, description: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Status</label>
                <select
                  className="w-full p-2 border rounded"
                  value={newModule.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setNewModule({...newModule, is_active: e.target.value === 'active'})}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="https://youtube.com/watch?v=..."
                  value={newModule.youtube_video_url}
                  onChange={(e) => setNewModule({...newModule, youtube_video_url: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Upload Image</label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Choose Image
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <span className="text-sm text-gray-500">
                    {newModule.imageFile ? newModule.imageFile.name : 'No file chosen'}
                  </span>
                </div>
                {newModule.imagePreview && (
                  <div className="mt-2 relative">
                    <img
                      src={newModule.imagePreview}
                      alt="Preview"
                      className="h-32 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Remove image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleAddModule}
                disabled={!newModule.title || !newModule.description}
              >
                Add Module
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Module Modal */}
      {showEditModal && editingModule && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Edit Training Module</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Title *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={editingModule.title}
                  onChange={(e) => setEditingModule({...editingModule, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Description *</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="3"
                  value={editingModule.description}
                  onChange={(e) => setEditingModule({...editingModule, description: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Status</label>
                <select
                  className="w-full p-2 border rounded"
                  value={editingModule.status}
                  onChange={(e) => setEditingModule({...editingModule, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">YouTube Video URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  placeholder="https://youtube.com/watch?v=..."
                  value={editingModule.youtube_video_url}
                  onChange={(e) => setEditingModule({...editingModule, youtube_video_url: e.target.value})}
                />
              </div>
              
              {/* Existing Images */}
              {editingModule.images && editingModule.images.length > 0 && (
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Existing Images</label>
                  <div className="grid grid-cols-2 gap-2">
                    {editingModule.images.map((image) => (
                      <div key={image.id} className="relative">
                        <img
                          src={image.image}
                          alt="Module image"
                          className="h-32 w-full object-cover rounded border"
                        />
                        {/* Removed delete button */}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload New Image */}
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Upload New Image</label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => editFileInputRef.current.click()}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Choose Image
                  </button>
                  <input
                    ref={editFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleEditImageUpload}
                  />
                  <span className="text-sm text-gray-500">
                    {editingModule.imageFile ? editingModule.imageFile.name : 'No file chosen'}
                  </span>
                </div>
                {editingModule.imagePreview && (
                  <div className="mt-2 relative">
                    <img
                      src={editingModule.imagePreview}
                      alt="Preview"
                      className="h-32 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveEditNewImage}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Remove image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={handleSaveEdit}
                disabled={!editingModule.title || !editingModule.description}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && moduleToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Delete Training Module</h2>
            <p className="mb-6">Are you sure you want to delete "{moduleToDelete.title}"? This action cannot be undone.</p>
            <div className="flex justify-end">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                onClick={confirmDeleteModule}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* View Module Details Modal */}
      {showViewModal && viewingModule && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">{viewingModule.title}</h2>
              <button 
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {/* Media viewer */}
            <div className="mb-6">
              {viewingModule.images && viewingModule.images.length > 0 && !showVideo && (
                <div className="relative">
                  <img 
                    src={viewingModule.images[currentImageIndex].image} 
                    alt={`${viewingModule.title} - Image ${currentImageIndex + 1}`}
                    className="w-full h-64 md:h-96 object-contain bg-gray-100 rounded"
                  />
                  
                  {viewingModule.images.length > 1 && (
                    <>
                      <button 
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full p-2 hover:bg-opacity-100"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                        </svg>
                      </button>
                      <button 
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full p-2 hover:bg-opacity-100"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </button>
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                        <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {viewingModule.images.length}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {viewingModule.youtube_video_url && showVideo && (
                <div className="aspect-w-16 aspect-h-9">
                  <iframe
                    src={`https://www.youtube.com/embed/${getYoutubeVideoId(viewingModule.youtube_video_url)}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-64 md:h-96 rounded"
                  ></iframe>
                </div>
              )}
              
              {/* Toggle buttons */}
              {viewingModule.youtube_video_url && viewingModule.images && viewingModule.images.length > 0 && (
                <div className="flex justify-center mt-4 space-x-4">
                  <button 
                    onClick={() => setShowVideo(false)}
                    className={`px-4 py-2 rounded ${!showVideo ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                  >
                    Images
                  </button>
                  <button 
                    onClick={() => setShowVideo(true)}
                    className={`px-4 py-2 rounded ${showVideo ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                  >
                    Video
                  </button>
                </div>
              )}
            </div>
            
            {/* Module details */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-2">Description</h3>
                <p className="text-gray-700">{viewingModule.description}</p>
              </div>
              
              <div className="flex justify-between items-center">
                <div>
                  <span className={`px-3 py-1 rounded-full text-sm ${viewingModule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {viewingModule.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  Created: {new Date(viewingModule.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col md:flex-row justify-between mb-6">
          <div className="w-full md:w-1/3 mb-4 md:mb-0">
            <input
              type="text"
              placeholder="Search modules..."
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

        </div>
        
        {/* Card Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pageItems.map(module => (
            <div key={module.id} className="bg-white border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-200 relative">
                {module.images && module.images.length > 0 ? (
                  <img 
                    src={module.images[0].image} 
                    alt={module.title} 
                    className="w-full h-full object-cover"
                  />
                ) : module.youtube_video_url ? (
                  <div className="w-full h-full">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYoutubeVideoId(module.youtube_video_url)}`}
                      title={module.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full object-cover"
                    ></iframe>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                )}
                {module.youtube_video_url && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                )}
                {module.images && module.images.length > 1 && (
                  <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded-full text-xs">
                    {module.images.length} images
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold">{module.title}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${module.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {module.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {module.description && (
                  <p className="text-sm text-gray-700 mb-4 line-clamp-2">{module.description}</p>
                )}
                <div className="flex justify-between space-x-2 mt-2">
                  <button 
                    onClick={() => handleViewDetails(module)} 
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                  >
                    View Details
                  </button>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleEditClick(module)} 
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(module)} 
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {filteredModules.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No modules found matching your filters.</p>
          </div>
        )}
      </div>
      <Pagination page={safePage} total={total} onPageChange={setCurrentPage} />
    </div>
  );
}

export default TrainingModules;




































































